import React from "react";
import Box from "@mui/material/Box";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Stack,
  TextField,
  Divider,
  Button,
  Paper,
  Typography,
  Select,
  MenuItem,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ItemsTable from "./items-table";
import { LoadCargo } from "../../../../domain/usecases/load-cargo";
import { NormalizedCargo, Route } from "../../../../domain/models/cargo-model";
import { LoadProductListByManyCodes } from "../../../../domain/usecases/load-product-list-by-many-codes";
import { normalizeCargo } from "../../../utils/normalize-cargo";
import {
  AddCargoError,
  SaveCargo,
} from "../../../../domain/usecases/save-cargo";
import { useErrorHandler } from "../../hooks";

type Props = {
  loadCargo: LoadCargo;
  loadProductListByManyCodes: LoadProductListByManyCodes;
  saveCargo: SaveCargo;
};

export const AddCargo: React.FC<Props> = ({
  loadCargo,
  loadProductListByManyCodes,
  saveCargo,
}) => {
  const [cargo, setCargo] = React.useState<NormalizedCargo | null>(null);
  const [cargoReference, setCargoReference] = React.useState<string>("");
  const [date, setDate] = React.useState<Dayjs>(dayjs(Date.now()));
  const [route, setRoute] = React.useState<Route | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [addError, setAddError] = React.useState<AddCargoError | null>(null);

  const handleError = useErrorHandler((error) => {
    alert("Aconteceu um erro. Tente novamente.");
  });

  const handleSearch = async () => {
    if (!cargoReference) return;
    setAddError(null);
    try {
      const cargo = await loadCargo.load(cargoReference);

      if (!cargo) return;
      const codes: string[] = [];
      cargo?.cargoItems.map((item) => {
        codes.push(item.eanCode);
      });

      cargo?.cargoItems.map((item) => {
        if (item.batches && item.batches.length > 0) {
          const totalAmount = item.batches?.reduce((acc, batch) => {
            acc += batch.amount;
            return acc;
          }, 0);

          if (totalAmount != item.totalAmount) {
            setError(
              `A quantidade reservada dos lotes do produto ${item.productReference} não coincide com o total da carga`
            );
          }
        }
      });

      const products = await loadProductListByManyCodes.load(codes);

      cargo?.cargoItems.map((item) => {
        const matchingProduct = products.find(
          (prod) => prod.eanCode === item.eanCode
        );

        if (matchingProduct) {
          item.productDescription = matchingProduct.description;
        }
      });
      const warehouseString = localStorage.getItem("currentWarehouse");
      const warehouse = warehouseString
        ? (JSON.parse(warehouseString) as {
            id: number;
            name: string;
          } | null)
        : undefined;

      const normalizedCargo = normalizeCargo(
        cargo,
        date.toDate().toISOString(),
        warehouse!.id
      );
      setCargo(normalizedCargo);
    } catch (error: any) {
      alert(error.message);
    }
  };

  const handleAdd = async () => {
    try {
      if (!cargo || !route) return;
      const input: NormalizedCargo = { ...cargo, route };
      const result = await saveCargo.save(input);
      if (!result.completed) {
        setAddError(result.error);
        return alert("Erro");
      }
      alert("Cargo adicionada com sucesso!");
      setCargo(null);
      setCargoReference("");
    } catch (error: any) {
      handleError(error);
    }
  };

  return (
    <Stack sx={{ m: "40px" }} spacing={2}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
          <DatePicker
            slotProps={{
              textField: { size: "small", sx: { backgroundColor: "white" } },
            }}
            value={date}
            onChange={(newValue) => {
              if (!newValue) return;
              setDate(newValue);
            }}
          />
        </LocalizationProvider>
      </Box>
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <TextField
          size="small"
          label={"Carga"}
          sx={{ backgroundColor: "white" }}
          value={cargoReference}
          onChange={(e) => {
            setCargoReference(e.target.value);
          }}
        />
      </Box>
      <Divider>
        <Button
          startIcon={<SearchRoundedIcon />}
          variant="contained"
          onClick={handleSearch}
        >
          Pesquisar
        </Button>
      </Divider>
      {cargo && (
        <>
          <Typography color="error">{error}</Typography>
          <Paper sx={{ p: "20px" }}>
            <Stack direction="row">
              <Stack sx={{ width: "30%" }}>
                <Typography fontWeight="bold">Informações Básicas</Typography>
                <Typography>{`Carga: ${cargo?.reference}`}</Typography>
                <Typography>{`Pedido: ${cargo?.orderReference}`}</Typography>
                <Typography>{`Cliente: ${cargo?.clientName}`}</Typography>
              </Stack>
              <div
                style={{
                  borderLeft: "1px solid  ",
                  height: "auto",
                  marginRight: "5px",
                }}
              ></div>
              <Stack sx={{ width: "60%" }}>
                <Typography fontWeight="bold">Endereço de Entrega</Typography>
                <Stack direction="row">
                  <Stack sx={{ width: "50%" }}>
                    <Typography>{`Endereço: ${cargo?.street}, ${cargo?.propertyNumber}`}</Typography>
                    <Typography>{`CEP: ${cargo?.cep}`}</Typography>
                    {cargo?.additionalInformation && (
                      <Typography>{`Complemento: ${cargo?.additionalInformation}`}</Typography>
                    )}
                  </Stack>
                  <Stack sx={{ width: "50%" }}>
                    <Typography>{`Estado: ${cargo?.federalUnit}`}</Typography>
                    <Typography>{`Bairro: ${cargo?.neighborhood}`}</Typography>
                    <Typography>{`Cidade: ${cargo?.city}`}</Typography>
                  </Stack>
                </Stack>
              </Stack>
              <div
                style={{
                  borderLeft: "1px solid  ",
                  height: "auto",
                  marginRight: "5px",
                }}
              ></div>
              <Stack sx={{ width: "10%" }}>
                <Typography fontWeight="bold">Rota</Typography>

                <Select
                  size="small"
                  sx={{ width: "100%" }}
                  value={route}
                  onChange={(e) => {
                    const value = e.target.value as unknown as Route;
                    setRoute(value);
                  }}
                >
                  <MenuItem value={Route.saoMateus}>São Mateus</MenuItem>
                  <MenuItem value={Route.teixeira}>Teixeira</MenuItem>
                  <MenuItem value={Route.linhares}>Linhares</MenuItem>
                  <MenuItem value={Route.interior}>Interior</MenuItem>
                </Select>
              </Stack>
            </Stack>
          </Paper>
          <ItemsTable cargoItems={cargo.cargoItems} />
          <Typography color="error">
            {addError?.data?.map((error) => {
              return `${error.item.productDescription} - ${
                error.error === "inventoryNotFound"
                  ? "Estoque não encontrado"
                  : "Quantidade insuficiente"
              } |`;
            })}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Button
              variant="contained"
              sx={{ width: "200px" }}
              disabled={!!error}
              onClick={handleAdd}
            >
              Adicionar
            </Button>
          </Box>
        </>
      )}
    </Stack>
  );
};
